import React, { FC } from 'react';

import classNames from 'classnames';

import { EvSection, EvSpinner, EvTitle } from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_COPY_URL_BUTTON_DAID,
	MOBILE_SDK_TEST_HEADER_DAID,
	MOBILE_SDK_TEST_TITLE_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { getCurrentMobileSdkFullUrl } from 'src/apps/mobile-sdk/helpers/MobileSdkDashboardHelpers';
import { useMobileSdkTestDetailsQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestDetailsQuery';
import { useSetMobileSdkBreadcrumbs } from 'src/apps/mobile-sdk/hooks/useSetMobileSdkBreadcrumbs';
import { CopyToClipboard } from 'src/common/components/copy-to-clipboard/CopyToClipboard';
import LinkIcon from 'src/common/components/icons/LinkIcon.svg?raw';
import { ReportInfo } from 'src/common/components/report-info/ReportInfo';

import { getMobileSdkInfoItems } from './MobileSdkSingleTestHeaderHelper';

import './MobileSdkSingleTestHeader.scss';

interface IMobileSdkSingleTestHeaderProps {
	testId: string;
	onTestNotFoundCallback: () => void;
}

export const MobileSdkSingleTestHeader: FC<IMobileSdkSingleTestHeaderProps> = ({
	testId,
	onTestNotFoundCallback
}) => {
	const testDetailsQuery = useMobileSdkTestDetailsQuery(testId);

	const { testName } = testDetailsQuery.data ?? {};

	useSetMobileSdkBreadcrumbs(testId, testName || testId);

	const isTestError =
		!testId ||
		testDetailsQuery.isError ||
		(testDetailsQuery.data === null && !testDetailsQuery.isLoading);

	if (isTestError) {
		onTestNotFoundCallback();
		return null;
	}

	return testDetailsQuery.isLoading ? (
		<EvSpinner />
	) : (
		<div className="mobile-sdk-single-test-header" data-automation-id={MOBILE_SDK_TEST_HEADER_DAID}>
			<div className="title-container">
				<EvTitle
					titleText={testName}
					headingLevel={1}
					className="main-title page-heading"
					dataAutomationId={MOBILE_SDK_TEST_TITLE_DAID}
				/>
				<CopyToClipboard
					renderJustIcon
					renderTooltip
					textToCopy={getCurrentMobileSdkFullUrl(testId)}
					className="copy-link-button"
					iconTitle="Copy Test URL"
					icon={LinkIcon}
					dataAutomationId={MOBILE_SDK_TEST_COPY_URL_BUTTON_DAID}
				/>
			</div>
			<EvSection
				ariaLabel={`Test ${testName} info`}
				skipLinkId="test-info-section"
				ariaLive="polite"
				className={classNames('dashboard-section', 'mobile-sdk-test-info-section')}
			>
				<ReportInfo
					titleText="Test Info"
					reportInfo={getMobileSdkInfoItems()}
					reportDetails={testDetailsQuery.data}
				/>
			</EvSection>
		</div>
	);
};
